// src/features/rooms/pages/CreateRoomPage.jsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Components
import RoomForm from '../components/RoomForm';

// Hooks
import { useRooms } from '../hooks/useRooms';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useAuth } from '../../../context/AuthContext';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { getCreatedId } from '../../../utils/helpers/getCreatedId';
import { FormPage, FormActions } from '../../../components/common/form';

export default function CreateRoomPage() {
  const { t } = useTranslation(['rooms', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { user } = useAuth();
  const { navigateBack } = useNavigationStack();

  // State
  const [formData, setFormData] = useState({
    name: '',
    area_m2: '',
    classification: '',
    notes: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  // API hooks
  const {
    createRoom,
    loading,
    error,
    clearError,
  } = useRooms();

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('rooms:validation.name_required');
    } else if (formData.name.length < 2) {
      errors.name = t('rooms:validation.name_min_length');
    } else if (formData.name.length > 255) {
      errors.name = t('rooms:validation.name_max_length');
    }

    if (!formData.area_m2 || formData.area_m2 === '') {
      errors.area_m2 = t('rooms:validation.area_required');
    } else if (parseFloat(formData.area_m2) <= 0) {
      errors.area_m2 = t('rooms:validation.area_positive');
    }

    if (!formData.classification) {
      errors.classification = t('rooms:validation.classification_required');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!user?.department.department_id) {
      showError(t('rooms:errors.no_department'));
      console.log("No dept")
      return;
    }
    console.log("validating form")

    try {
      const roomData = {
        name: formData.name.trim(),
        area_m2: parseFloat(formData.area_m2),
        classification: formData.classification,
        notes: formData.notes.trim() || null,
      };

      const result = await createRoom(roomData);

      if (result.success) {
        showSuccess(t('rooms:success.room_created'));
        // Open the new record (replace, so Back skips the filled-in form)
        const newId = getCreatedId(result, 'room_id');
        navigate(newId ? `/rooms/${newId}` : '/rooms', { replace: true });
      } else {
        showError(result.error || t('rooms:errors.create_failed'));
      }
    } catch (error) {
        console.log(error)
      showError(t('rooms:errors.unexpected_error'));
    }
  }, [validateForm, formData, user, createRoom, showSuccess, showError, navigate, t]);

  const handleCancel = useCallback(() => {
    navigateBack('/rooms');
  }, [navigate]);

  return (
    <FormPage
      title={t('rooms:add_room')}
      subtitle={t('rooms:form.create_subtitle')}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
    >
      <RoomForm
        formData={formData}
        onInputChange={handleInputChange}
        validationErrors={validationErrors}
        loading={loading}
        mode="create"
      />
      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={loading ? t('rooms:actions.creating') : t('rooms:actions.create_room')}
        submitIcon={<SaveIcon />}
        loading={loading}
      />
    </FormPage>
  );
}
